<template>
  <div>
    <div class="p-box">
      <div class="p-box__body">
        <Info>{{ $t('messages.qualityCheckDescription') }}</Info>
        <Loading v-show="loading" />
        <div v-if="!loading">
          <div v-for="meta in attributeMetadataExceptUrl" :key="meta.groupDisplayName" class="attributeGroup">
            <div class="attributeGroup__groupDisplayName">
              {{ meta.groupDisplayName }}
            </div>
            <div v-for="data in meta.groupData" :key="data.parent" class="attributeGroup__item">
              <MultipleChoiceCheckBox
                v-if="data.valueType === valueTypes[0]"
                :check-status="extractValue(data)"
                :label="extractLabel(data)"
                @change-status="changeAttribute({ type: data.valueType, payload: data })"
              />
              <RadioButtonBox
                v-if="data.valueType === valueTypes[1]"
                :items="extractValueMetadata(data)"
                :label="data.displayName"
                :value="extractEnumValue(data)"
                @change="item => handleChangeEnumValue(item,data)"
              />
              <div v-if="data.valueType === valueTypes[2]" class="repeatedEnumValueBox">
                <div class="repeatedEnumValueBox__label">
                  {{ data.displayName }}
                </div>
                <MultipleChoiceCheckBox
                  v-for="valueMetadata in data.valueMetadata"
                  :key="valueMetadata.displayName"
                  :check-status="extractRepeatedEnumValue(valueMetadata, data)"
                  :label="valueMetadata.displayName"
                  class="repeatedEnumValueBox__multipleChoiceCheckBox"
                  @change-status="handleChangeRepeatedEnumValue(valueMetadata, data)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="p-box__footer">
        <div class="c-button c-button--right">
          <router-link :to="{ name: 'CompaniesLocationStore', params: { company_uuid: currentCompanyUuid, store_uuid: currentStoreUuid } }" class="c-button__item c-button__item--outline-cancel">
            {{ $t('labels.cancel') }}
          </router-link>
          <button class="c-button__item c-button__item--primary" @click="submit">
            {{ $t('labels.submitFilterCond') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Info from '../../../../../components/Info'
import { mapState, mapActions, mapGetters } from 'vuex'
import MultipleChoiceCheckBox from '../../../../../components/MultipleChoiceCheckBox'
import RadioButtonBox from '../../../../../components/RadioButtonBox'
import { gmbLocationAttributeValueTypes } from '../../../../../stores/companies/store/gmb/attributes'
import Loading from '../../../../../components/Loading'

export default {
  name: 'CompaniesLocationStoreAttributesEdit',
  components: {
    MultipleChoiceCheckBox,
    Info,
    RadioButtonBox,
    Loading
  },
  emits: ['update-title', 'update-breadcrumbs'],
  computed: {
    ...mapState({
      store: state => state.companiesLocationStore.data,
      attributes: state => state.companiesStoreGmbAttributes.attributes,
      loading: state => state.companiesStoreGmbAttributes.loading,
    }),
    ...mapGetters({
      attributeMetadataExceptUrl: 'companiesStoreGmbAttributes/attributeMetadataExceptUrl'
    }),
    valueTypes() {
      return gmbLocationAttributeValueTypes
    },
    currentCompanyUuid() {
      return this.$route.params.company_uuid
    },
    currentStoreUuid() {
      return this.$route.params.store_uuid
    }
  },
  async created() {
    await Promise.all([
      this.readStore({ company_uuid: this.currentCompanyUuid, store_uuid: this.currentStoreUuid }),
      this.readGmbAttributes(),
    ])
    this.$emit(
      'update-title',
      this.$t('pages.CompaniesLocationStoreAttributesEdit.title', {
        name: this.store.attributes.name,
      })
    )
  },
  methods: {
    ...mapActions({
      readStore: 'companiesLocationStore/readStore',
      readGmbAttributes: 'companiesStoreGmbAttributes/readGmbAttributes',
      changeAttribute: 'companiesStoreGmbAttributes/reduceAttribute',
      updateGmbAttribute: 'companiesStoreGmbAttributes/updateGmbAttribute'
    }),
    handleChangeEnumValue(item, data) {
      this.changeAttribute({
        type: data.valueType,
        payload: {
          metadata: data,
          newValue: item.value
        }
      })
    },
    async submit() {
      this.updateGmbAttribute(this.attributes)
    },
    handleChangeRepeatedEnumValue(valueMetadata, data) {
      this.changeAttribute({
        type: data.valueType,
        payload: {
          metadata: data,
          valueMetadata: valueMetadata,
        }
      })
    },
    extractRepeatedEnumValue(valueMetadata, data) {
      if (!data.repeatedEnumValue) return 'noSetting'
      const isSet = data.repeatedEnumValue.setValues.includes(valueMetadata.value)
      const isUnset = data.repeatedEnumValue.unsetValues.includes(valueMetadata.value)
      if (!isSet && !isUnset) {
        return 'noSetting'
      } else if (isSet) {
        return true
      } else if(isUnset) {
        return false
      } else {
        throw new Error('Invalid RepeatedEnumValue')
      }
    },
    extractEnumValue(data) {
      return data.values ? data.values[0] : 'noSetting'
    },
    extractValueMetadata(data) {
      const items = data.valueMetadata.map(v => {
        return {
          id: v.displayName,
          value: v.value,
          text: v.displayName,
        }
      })
      items.push({
        id: 'noSetting',
        value: 'noSetting',
        text: '設定無し',
      })
      return items
    },
    extractValue(data) {
      if (!data.values) return 'noSetting'
      const value = data.values[0]
      const displayValueMeta = data.valueMetadata.find(meta => meta.value === value)
      // なぜかBOOLの時、trueしかGMB側にない場合がある(例. is_owned_by_women)
      if (!displayValueMeta) return false
      return displayValueMeta.value
    },
    extractLabel(data) {
      if (!data.values) return data.displayName
      const value = data.values[0]
      const displayValueMeta = data.valueMetadata.find(meta => meta.value === value)
      // なぜかBOOLの時、trueしかGMB側にない場合がある(例. is_owned_by_women)
      if (!displayValueMeta) return data.displayName
      return displayValueMeta.displayName
    }
  },
}
</script>

<style lang="scss" scoped>
.attributeGroup:not(:last-child) {
  border-bottom: 1px solid #E4E7EB;
}
.attributeGroup {
  padding: 32px;
  &__groupDisplayName {
    font-weight: bold;
    font-size: 18px;
  }
  &__item {
    margin-top: 16px;
  }
}
.repeatedEnumValueBox {
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  padding: 16px 24px;
  &__label {
    font-weight: bold;
  }
  &__multipleChoiceCheckBox {
    margin-top: 16px;
  }
}
</style>
